import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Loader2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { applyApiFieldErrors } from '@/lib/form-errors';

import { useResetUserPassword } from './useUsers';
import { passwordResetSchema, type EfsUser, type PasswordResetInput } from './types';

const NS = 'settings';

interface UserPasswordDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  user: EfsUser;
}

export const UserPasswordDialog = ({ open, onOpenChange, user }: UserPasswordDialogProps) => {
  const { t } = useTranslation(NS);
  const reset = useResetUserPassword();

  const form = useForm<PasswordResetInput>({
    resolver: zodResolver(passwordResetSchema),
    defaultValues: { new_password: '' },
  });

  useEffect(() => {
    if (!open) form.reset({ new_password: '' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const submit = form.handleSubmit(async (values) => {
    form.clearErrors();
    try {
      await reset.mutateAsync({ id: user.user_id, body: values });
      onOpenChange(false);
    } catch (error) {
      applyApiFieldErrors(form, error, ['new_password']);
    }
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('efs.settings.users.dialog.password_title')}</DialogTitle>
          <DialogDescription>{user.email}</DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={submit} className="space-y-4" noValidate>
            <FormField
              control={form.control}
              name="new_password"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('efs.settings.users.fields.password')}</FormLabel>
                  <FormControl>
                    <Input type="password" autoComplete="new-password" autoFocus {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {form.formState.errors.root?.message ? (
              <p className="text-sm font-medium text-destructive">
                {String(form.formState.errors.root.message)}
              </p>
            ) : null}

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={reset.isPending}
              >
                {t('efs.common.actions.cancel')}
              </Button>
              <Button type="submit" disabled={reset.isPending}>
                {reset.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                {t('efs.settings.users.actions.reset_password')}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
